import React, { createContext, useContext, useEffect, useState } from 'react';
import { companyService, type CompanyDetails, type CompanyBranding, type CompanyLandingPage } from '@/services/companyService';
import { applyBrandingCSS, resetBrandingCSS } from '@/lib/branding';

interface WidgetContextType {
  isWidget: boolean;
  companyId: string | null;
  targetId: string | null;
  // New branding properties
  companyDetails: CompanyDetails | null;
  branding: CompanyBranding | null;
  landingPage: CompanyLandingPage | null;
  isLoadingCompany: boolean;
}

const WidgetContext = createContext<WidgetContextType>({
  isWidget: false,
  companyId: null,
  targetId: null,
  companyDetails: null,
  branding: null,
  landingPage: null,
  isLoadingCompany: false,
});

export const useWidget = () => {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidget must be used within a WidgetProvider');
  }
  return context;
};

interface WidgetProviderProps {
  children: React.ReactNode;
}

export const WidgetProvider: React.FC<WidgetProviderProps> = ({ children }) => {
  const [isWidget, setIsWidget] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);
  const [branding, setBranding] = useState<CompanyBranding | null>(null);
  const [landingPage, setLandingPage] = useState<CompanyLandingPage | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);

  // Fetch company details and apply branding when companyId changes
  useEffect(() => {
    const fetchCompanyAndApplyBranding = async () => {
      if (!companyId) {
        // Reset branding when no company
        resetBrandingCSS();
        setCompanyDetails(null);
        setBranding(null);
        setLandingPage(null);
        return;
      }

      setIsLoadingCompany(true);
      try {
        const details = await companyService.getCompanyDetails(companyId);
        if (details) {
          setCompanyDetails(details);
          setBranding(details.branding || null);
          setLandingPage(details.landing_page || null);

          // Apply branding CSS
          if (details.branding) {
            applyBrandingCSS(details.branding);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching company for branding:', error);
      } finally {
        setIsLoadingCompany(false);
      }
    };

    fetchCompanyAndApplyBranding();
  }, [companyId]);

  useEffect(() => {
    // Check if we're running as a widget or have company context
    const checkWidgetMode = async () => {
      // Check URL parameters for widget mode using window.location
      const urlParams = new URLSearchParams(window.location.search);
      const widgetCompanyId = urlParams.get('companyId');
      const widgetTargetId = urlParams.get('targetId');
      const embedParam = urlParams.get('embed');

      // Widget mode requires BOTH companyId AND embed=true
      // This allows direct links (?companyId=xxx) to use normal site features
      // while iframe embeds (?companyId=xxx&embed=true) use widget-specific behavior
      const isEmbedded = embedParam === 'true';

      // Priority 1: URL parameter companyId
      if (widgetCompanyId) {
        setCompanyId(widgetCompanyId);
        setTargetId(widgetTargetId);

        if (isEmbedded) {
          setIsWidget(true);
          console.log('🏢 Widget mode (embedded):', { companyId: widgetCompanyId, targetId: widgetTargetId });
        } else {
          console.log('🏢 Company context (direct link):', { companyId: widgetCompanyId });
        }

        // Store widget context in sessionStorage to persist through auth state changes
        sessionStorage.setItem('widget_context', JSON.stringify({
          isWidget: isEmbedded,
          companyId: widgetCompanyId,
          targetId: widgetTargetId
        }));
        return;
      }

      // Priority 2: Check sessionStorage for persisted context
      const storedContext = sessionStorage.getItem('widget_context');
      if (storedContext) {
        try {
          const { isWidget: storedIsWidget, companyId: storedCompanyId, targetId: storedTargetId } = JSON.parse(storedContext);
          console.log('🏢 Restoring widget context from session storage:', { isWidget: storedIsWidget, storedCompanyId, storedTargetId });
          setIsWidget(storedIsWidget);
          setCompanyId(storedCompanyId);
          setTargetId(storedTargetId);
          return;
        } catch (e) {
          console.warn('Failed to parse stored widget context:', e);
        }
      }

      // Priority 3: Domain-based company lookup (for white-label)
      const hostname = window.location.hostname;
      // Skip domain lookup for localhost and main domain
      if (hostname !== 'localhost' && !hostname.includes('127.0.0.1')) {
        try {
          console.log('🌐 Checking for company by domain:', hostname);
          const companyByDomain = await companyService.getCompanyByDomain(hostname);
          if (companyByDomain) {
            console.log('🏢 Found company by domain:', companyByDomain.name);
            setCompanyId(companyByDomain.id);
            setCompanyDetails(companyByDomain);
            setBranding(companyByDomain.branding || null);
            setLandingPage(companyByDomain.landing_page || null);

            if (companyByDomain.branding) {
              applyBrandingCSS(companyByDomain.branding);
            }
            return;
          }
        } catch (error) {
          console.error('❌ Error looking up company by domain:', error);
        }
      }

      // No company context found - clear state
      setIsWidget(false);
      setCompanyId(null);
      setTargetId(null);
      sessionStorage.removeItem('widget_context');
    };

    checkWidgetMode();

    // Listen for URL changes (back/forward navigation)
    const handleLocationChange = () => {
      checkWidgetMode();
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  return (
    <WidgetContext.Provider value={{
      isWidget,
      companyId,
      targetId,
      companyDetails,
      branding,
      landingPage,
      isLoadingCompany
    }}>
      {children}
    </WidgetContext.Provider>
  );
}; 